import React, { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import {
  BasePatcherProps,
  DEFAULT_DEBOUNCE_TIMEOUT,
  WithPassedPatchableGroupProps
} from './PatchableGroup';
import {
  LocaleDatePicker,
  LocaleDatePickerProps
} from '@ianesthesie-kit/locale-date-picker';
import { formatDateOrNull, parseDateOrNull } from '@ianesthesie-kit/core';
import { TextField } from '@mui/material';
import { isValid } from 'date-fns';
import { AppConfigService } from '@/config';
import { useCurrentHospitalCountry } from '@/modules/appContext';

export type DateInputPatcherProps = BasePatcherProps &
  Omit<LocaleDatePickerProps, 'value' | 'onChange' | keyof BasePatcherProps>;

const DateInputPatcherComponent = <T,>({
  /* eslint-disable @typescript-eslint/no-unused-vars */
  name,
  error,
  wrapper,
  patchable,
  loading,
  hideLoadingOverlay,
  sourceType,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  ...restProps
}: WithPassedPatchableGroupProps<T, DateInputPatcherProps>) => {
  const currentCountry =
    useCurrentHospitalCountry() ??
    AppConfigService.getInstance().countryBasedOnEnv;
  const [value, setValue] = useState(parseDateOrNull(patchable?.currentValue));
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  if (!patchable) {
    throw new Error(
      'A Patcher component may be used as a child of a PatchableGroup'
    );
  }

  useEffect(() => {
    if (patchable?.currentValue?.endsWith('Z')) {
      setValue(new Date(patchable.currentValue));
    } else {
      setValue(parseDateOrNull(patchable?.currentValue));
    }
  }, [patchable?.currentValue]);

  const doPatch = (v: Date | null) => {
    const date = formatDateOrNull(v);
    if (!loading && patchable && patchable.currentValue !== date) {
      const { onPatch } = patchable;
      onPatch({ [name]: date });
    }
  };
  const onInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const potentialDateText = e.target.value;
    if (potentialDateText) {
      const format = currentCountry === 'US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
      const potentialDate = parseDateOrNull(e.target.value, format);
      if (potentialDate) {
        timerRef.current = setTimeout(() => {
          doPatch(potentialDate);
        }, DEFAULT_DEBOUNCE_TIMEOUT);
      }
    }
  };

  const onInputBlur = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (patchable.readOnly) {
      return;
    }
    if (isValid(value)) {
      doPatch(value);
    }
  };

  return (
    <UnconnectedDateInputPatcher
      loading={loading}
      {...restProps}
      onChange={setValue}
      onAccept={doPatch}
      readOnly={patchable.readOnly}
      InputProps={{
        error: !!error,
        onChange: onInputChange,
        onBlur: onInputBlur
      }}
      value={value}
      disabled={restProps.disabled || loading}
    />
  );
};

export const UnconnectedDateInputPatcher = (props: LocaleDatePickerProps) => (
  <LocaleDatePicker
    clearOnCancel={false}
    setTodayOnDoubleClick={!props.readOnly && 'ifNotSet'}
    {...props}
    renderInput={(p) => (
      <TextField
        {...p}
        size="small"
        fullWidth
        color="riskNone"
        inputProps={{ ...p.inputProps }}
      />
    )}
  />
);

export const DateInputPatcher = (props: DateInputPatcherProps) => (
  <DateInputPatcherComponent {...props} />
);
